import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  type Auth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import {
  generateSessionKey,
  clearSessionKey,
  storeEncryptedToken,
  retrieveEncryptedToken,
  clearEncryptedToken,
  isEncryptionInitialized,
} from './token-encryption';
import { logger } from './logger';
import { getStorage } from './storage';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validate environment variables
if (!firebaseConfig.apiKey) {
  throw new Error('Missing environment variable: VITE_FIREBASE_API_KEY');
}

if (!firebaseConfig.projectId) {
  throw new Error('Missing environment variable: VITE_FIREBASE_PROJECT_ID');
}

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
  throw error;
}

// Configure Google Auth Provider with required scopes
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.addScope('https://www.googleapis.com/auth/drive.file');
googleProvider.addScope('https://www.googleapis.com/auth/calendar.events');

// Set custom parameters for Google OAuth
googleProvider.setCustomParameters({
  access_type: 'offline',
  prompt: 'consent',
});

// Store the OAuth access token and expiry information
interface TokenData {
  accessToken: string;
  expiresAt: number; // Unix timestamp in milliseconds
}

const TOKEN_STORAGE_KEY = 'nidonote-oauth-token';
const TOKEN_EXPIRY_KEY = 'nidonote-oauth-expiry';

let tokenData: TokenData | null = null;
let tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let tokenRefreshListeners: Array<(token: string | null) => void> = [];

/**
 * Save token to storage with encryption
 * Requires session encryption key to be initialized
 */
const saveTokenToStorage = async (token: string, expiresAt: number) => {
  try {
    if (!isEncryptionInitialized()) {
      logger.error('Firebase', 'Cannot save token - encryption not initialized');
      return;
    }

    const storage = await getStorage();
    await storeEncryptedToken(TOKEN_STORAGE_KEY, token);
    await storage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());

    logger.auth('Token saved to storage (encrypted)');
  } catch (error) {
    logger.error('Firebase', 'Failed to save token to storage:', error);
  }
};

/**
 * Load token from storage with decryption
 * Returns null if token doesn't exist, is expired, or decryption fails
 */
const loadTokenFromStorage = async (): Promise<TokenData | null> => {
  try {
    if (!isEncryptionInitialized()) {
      logger.warn('Firebase', 'Cannot load token - encryption not initialized (user not logged in yet)');
      return null;
    }

    const storage = await getStorage();
    const token = await retrieveEncryptedToken(TOKEN_STORAGE_KEY);
    const expiryStr = await storage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !expiryStr) {
      return null;
    }

    const expiresAt = parseInt(expiryStr, 10);

    if (isNaN(expiresAt)) {
      return null;
    }

    // Check if token is expired
    const now = Date.now();
    if (now >= expiresAt) {
      logger.auth('Stored token is expired');
      await clearTokenFromStorage();
      return null;
    }

    logger.auth('Loaded valid token from storage (decrypted)');
    return { accessToken: token, expiresAt };
  } catch (error) {
    logger.error('Firebase', 'Failed to load token from storage:', error);
    return null;
  }
};

/**
 * Clear token from storage
 */
const clearTokenFromStorage = async () => {
  try {
    const storage = await getStorage();
    await clearEncryptedToken(TOKEN_STORAGE_KEY);
    await storage.removeItem(TOKEN_EXPIRY_KEY);

    logger.auth('Token cleared from storage');
  } catch (error) {
    logger.error('Firebase', 'Failed to clear token from storage:', error);
  }
};

/**
 * Subscribe to token refresh events
 * Useful for components that need to know when token changes
 */
export const onTokenRefresh = (callback: (token: string | null) => void): (() => void) => {
  tokenRefreshListeners.push(callback);

  // Return unsubscribe function
  return () => {
    tokenRefreshListeners = tokenRefreshListeners.filter(cb => cb !== callback);
  };
};

/**
 * Notify all listeners of token change
 */
const notifyTokenRefresh = (token: string | null) => {
  tokenRefreshListeners.forEach(callback => {
    try {
      callback(token);
    } catch (error) {
      console.error('[Firebase] Error in token refresh listener:', error);
    }
  });
};

/**
 * Schedule automatic token refresh
 * Refreshes 5 minutes before expiry using Firebase's getIdToken(true) for silent renewal
 */
const scheduleTokenRefresh = () => {
  // Clear any existing timer
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }

  if (!tokenData) {
    console.log('[Firebase] No token data, skipping refresh schedule');
    return;
  }

  const now = Date.now();
  const timeUntilExpiry = tokenData.expiresAt - now;
  const refreshBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Calculate when to refresh (5 minutes before expiry, or immediately if < 5 min remaining)
  const timeUntilRefresh = Math.max(0, timeUntilExpiry - refreshBuffer);

  console.log('[Firebase] Scheduling token refresh in', Math.round(timeUntilRefresh / 1000 / 60), 'minutes');
  console.log('[Firebase] Token will expire at:', new Date(tokenData.expiresAt).toLocaleTimeString());

  tokenRefreshTimer = setTimeout(async () => {
    console.log('[Firebase] 🔄 Token refresh timer triggered at 55-minute mark');
    console.log('[Firebase] ⏰ Automatic re-authentication will occur to renew Drive/Calendar access');

    const user = auth.currentUser;

    if (!user) {
      console.error('[Firebase] ❌ No authenticated user, cannot refresh token');
      notifyTokenRefresh(null);
      return;
    }

    try {
      // Step 1: Refresh Firebase ID token (silent, always works)
      console.log('[Firebase] Refreshing Firebase session...');
      await user.getIdToken(true);
      console.log('[Firebase] ✅ Firebase session refreshed');

      // Step 2: Get new OAuth access token via popup re-authentication
      // This is REQUIRED for Google Drive/Calendar API access
      console.log('[Firebase] 🔑 Re-authenticating to renew Drive/Calendar access...');
      console.log('[Firebase] A popup will appear - please click "Continue" to maintain access');

      const result = await signInWithPopup(auth, googleProvider);
      const credential = GoogleAuthProvider.credentialFromResult(result);

      if (credential?.accessToken) {
        // Successfully got new access token
        const expiresIn = 3600 * 1000; // 1 hour
        const expiresAt = Date.now() + expiresIn;

        tokenData = {
          accessToken: credential.accessToken,
          expiresAt,
        };

        await saveTokenToStorage(credential.accessToken, expiresAt);

        console.log('[Firebase] ✅ OAuth access token renewed successfully!');
        console.log('[Firebase] ✅ Drive/Calendar access extended for another hour');
        console.log('[Firebase] Next refresh at:', new Date(expiresAt - (5 * 60 * 1000)).toLocaleTimeString());

        // Schedule next refresh
        scheduleTokenRefresh();

        // Notify listeners of successful renewal
        notifyTokenRefresh(tokenData.accessToken);
      } else {
        console.error('[Firebase] ❌ No access token received from re-authentication');
        notifyTokenRefresh(null);
      }
    } catch (error: any) {
      console.error('[Firebase] ❌ Token refresh failed:', error);

      // Handle different error cases
      if (error?.code === 'auth/popup-closed-by-user') {
        console.warn('[Firebase] ⚠️ User closed re-authentication popup');
        console.warn('[Firebase] ⚠️ Token will expire - Drive/Calendar sync will stop working');
      } else if (error?.code === 'auth/popup-blocked') {
        console.error('[Firebase] ❌ Popup blocked by browser');
        console.error('[Firebase] Please allow popups for this site to maintain Drive/Calendar access');
      } else if (error?.code === 'auth/network-request-failed') {
        console.error('[Firebase] ❌ Network error during refresh - will retry in 2 minutes');
        // Retry in 2 minutes
        tokenRefreshTimer = setTimeout(() => {
          scheduleTokenRefresh();
        }, 2 * 60 * 1000);
        return;
      }

      // For other errors, notify that token is expired
      notifyTokenRefresh(null);
    }
  }, timeUntilRefresh);
};

/**
 * Refresh the OAuth access token
 *
 * NOTE: This requires user interaction (popup) because Firebase Auth doesn't provide
 * a way to silently refresh Google OAuth tokens. The refresh token is managed by Firebase
 * internally but not exposed to us.
 *
 * For automatic refresh after 7+ days, we would need:
 * 1. A backend service to store and use refresh tokens
 * 2. OR use Google Identity Services (gis) library directly instead of Firebase Auth
 *
 * Current behavior: Shows popup for re-authentication when token expires
 */
export const refreshAccessToken = async (silent: boolean = false): Promise<string | null> => {
  const user = auth.currentUser;

  if (!user) {
    console.warn('[Firebase] No user authenticated, cannot refresh token');
    return null;
  }

  try {
    console.log('[Firebase] Refreshing access token...');

    if (silent) {
      // For silent refreshes, we can't actually do a true silent refresh with Firebase
      // Just return null and let the error handling guide the user
      console.log('[Firebase] Silent refresh requested, but popup required for Google OAuth token refresh');
      return null;
    }

    // Show popup to re-authenticate
    console.log('[Firebase] ⚠️ Showing re-authentication popup to refresh Google Drive access...');
    const result = await signInWithPopup(auth, googleProvider);

    const credential = GoogleAuthProvider.credentialFromResult(result);

    if (credential?.accessToken) {
      // Store new token with expiry time
      const expiresIn = 3600 * 1000; // 1 hour in milliseconds
      const expiresAt = Date.now() + expiresIn;

      tokenData = {
        accessToken: credential.accessToken,
        expiresAt,
      };

      // Save to storage for persistence
      await saveTokenToStorage(credential.accessToken, expiresAt);

      console.log('[Firebase] ✅ Token refreshed successfully');
      console.log('[Firebase] New token expires at:', new Date(tokenData.expiresAt).toISOString());

      // Schedule next refresh
      scheduleTokenRefresh();

      // Notify listeners
      notifyTokenRefresh(tokenData.accessToken);

      return tokenData.accessToken;
    } else {
      console.error('[Firebase] ❌ No access token in refresh credential');
      await clearTokenFromStorage();
      notifyTokenRefresh(null);
      return null;
    }
  } catch (error: any) {
    console.error('[Firebase] ❌ Error refreshing token:', error);

    // Handle specific error cases
    if (error?.code === 'auth/popup-closed-by-user') {
      console.log('[Firebase] User closed refresh popup - will retry on next API call');
    } else if (error?.code === 'auth/popup-blocked') {
      console.error('[Firebase] Popup blocked during token refresh - please allow popups');
    } else if (error?.code === 'auth/network-request-failed') {
      console.error('[Firebase] Network error during token refresh');
    }

    notifyTokenRefresh(null);
    return null;
  }
};

/**
 * Check if the current token is expired or about to expire
 */
export const isTokenExpired = (): boolean => {
  if (!tokenData) {
    return true;
  }

  const now = Date.now();
  const buffer = 2 * 60 * 1000; // 2 minute buffer

  return now >= (tokenData.expiresAt - buffer);
};

/**
 * Get time remaining until token expires (in milliseconds)
 */
export const getTokenTimeRemaining = (): number => {
  if (!tokenData) {
    return 0;
  }

  return Math.max(0, tokenData.expiresAt - Date.now());
};

/**
 * Sign in with Google OAuth
 * Using popup - simpler and returns the access token directly
 */
export const signInWithGoogle = async (): Promise<User> => {
  try {
    logger.auth('Starting sign in with popup...');
    const result = await signInWithPopup(auth, googleProvider);

    logger.auth('Sign in successful:', {
      user: result.user.email,
      providerId: result.providerId,
    });

    // Generate session encryption key for token storage
    // This key is stored in memory only and cleared on logout/session end
    generateSessionKey();
    logger.auth('Session encryption key generated for secure token storage');

    // Extract the OAuth access token from the credential
    const credential = GoogleAuthProvider.credentialFromResult(result);
    logger.debug('Firebase', 'Credential:', {
      hasCredential: !!credential,
      hasAccessToken: !!credential?.accessToken,
    });

    if (credential?.accessToken) {
      // Store token with expiry time (tokens typically expire in 1 hour = 3600 seconds)
      const expiresIn = 3600 * 1000; // 1 hour in milliseconds
      const expiresAt = Date.now() + expiresIn;

      tokenData = {
        accessToken: credential.accessToken,
        expiresAt,
      };

      // Save to storage with encryption
      await saveTokenToStorage(credential.accessToken, expiresAt);

      logger.auth('✅ Stored OAuth access token for Drive/Calendar (encrypted)');
      logger.auth('Token expires at:', new Date(tokenData.expiresAt).toISOString());

      // Schedule token refresh 5 minutes before expiry
      scheduleTokenRefresh();

      // Notify listeners
      notifyTokenRefresh(tokenData.accessToken);
    } else {
      logger.warn('Firebase', '⚠️ No access token in credential!');
      await clearTokenFromStorage();
    }

    return result.user;
  } catch (error) {
    logger.error('Firebase', '❌ Error signing in:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOut = async (): Promise<void> => {
  try {
    logger.auth('Signing out...');

    // Clear token refresh timer
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }

    // Clear session encryption key (stored in memory)
    clearSessionKey();

    await firebaseSignOut(auth);
    tokenData = null; // Clear the OAuth token
    await clearTokenFromStorage(); // Clear encrypted token from storage

    // Notify listeners
    notifyTokenRefresh(null);

    logger.auth('✅ Signed out successfully');
  } catch (error) {
    logger.error('Firebase', 'Error signing out:', error);
    throw error;
  }
};

/**
 * Get the currently authenticated user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!auth.currentUser;
};

/**
 * Listen to auth state changes
 * @param callback Function to call when auth state changes
 * @returns Unsubscribe function
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get the OAuth access token for Google Drive/Calendar API calls
 * This is the actual OAuth token, NOT the Firebase ID token
 * Automatically refreshes if token is expired
 */
export const getAccessToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('[Firebase] No user authenticated, cannot get access token');
    await clearTokenFromStorage();
    return null;
  }

  // If we don't have tokenData in memory, try to load from storage
  if (!tokenData) {
    console.log('[Firebase] No token in memory, attempting to load from storage...');
    const storedToken = await loadTokenFromStorage();

    if (storedToken) {
      tokenData = storedToken;
      console.log('[Firebase] ✅ Restored token from storage');
      console.log('[Firebase] Token expires at:', new Date(tokenData.expiresAt).toISOString());

      // Schedule refresh for the restored token
      scheduleTokenRefresh();

      // Notify listeners
      notifyTokenRefresh(tokenData.accessToken);
    } else {
      console.warn('[Firebase] ⚠️ No valid token in storage. Attempting to refresh...');

      // Try to refresh the token (will show popup)
      const newToken = await refreshAccessToken();

      if (!newToken) {
        console.error('[Firebase] ❌ Failed to refresh token. User needs to sign in again.');
        return null;
      }

      return newToken;
    }
  }

  // If token is expired or about to expire, refresh it
  if (isTokenExpired()) {
    console.log('[Firebase] Token expired or expiring soon, refreshing...');
    const newToken = await refreshAccessToken();

    if (!newToken) {
      console.error('[Firebase] Failed to refresh token');
      await clearTokenFromStorage();
      return null;
    }

    return newToken;
  }

  return tokenData.accessToken;
};

// Export Firebase instances
export { app, auth };
export type { User } from 'firebase/auth';
